import type { Model } from "@/schemas/model.schema";

/**
 * Pure, framework-free "who is billing this model" logic for the LLM picker
 * (AI-1067). Kept out of any Vue component so it can be unit-tested in
 * isolation, exactly like `model-ratings.ts`.
 *
 * The whole point of this file is that the answer comes from the SERVER's
 * `model.byok` object and nothing else. The picker used to leave the question
 * unanswered, and a missing cost star ("no price in the rate deck") was read by
 * a customer as "this is the BYOK model" - while in reality every model of a
 * provider with a configured credential is billed by that provider.
 */

/**
 * - `platform`: SipPulse bills this model (also the fallback for a server that
 *   does not send `byok` at all)
 * - `byok_active`: the org's credential for this provider is configured, so the
 *   provider bills it directly
 * - `byok_available`: the model can run on the org's own key, but today the
 *   platform bills it
 * - `byok_required`: it only runs on the org's own key, and that key is missing
 */
export type BillingState = "platform" | "byok_active" | "byok_available" | "byok_required";

export function billingStateOf(model: Model): BillingState {
  const byok = model.byok;
  if (!byok) return "platform";
  // `configured` wins over `required`: once the credential exists the provider
  // is already billing, whether or not the model could have run without it.
  if (byok.configured) return "byok_active";
  if (byok.required) return "byok_required";
  if (byok.supported) return "byok_available";
  return "platform";
}

/**
 * A model the org cannot run: it needs its own provider key and that key is not
 * configured. The picker still lists it (that is how the user learns the key is
 * what is missing) but must not let it be selected.
 */
export function isBillingBlocked(state: BillingState): boolean {
  return state === "byok_required";
}
